import { useCallback, useRef, useState } from 'react';

import { GenericItem, GroupHeader, VirtualProps } from '../types';

const MENU_ITEM_HEIGHT = 36;
const MENU_ITEM_WITH_SUBTEXT_HEIGHT = 56;

/**
 * A hook used to manage all logic needed for a virtualized Dropdown.
 * Returns properties in the format @tanstack/react-virtual expects.
 */
export default <TItem extends GenericItem>({
  menuItems,
  isGroupHeader,
  showEmptyGroups,
  showGroupHeaders,
}: {
  menuItems: TItem[];
  isGroupHeader: (item: unknown) => item is GroupHeader;
  showEmptyGroups?: boolean;
  showGroupHeaders?: boolean;
}): VirtualProps => {
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const virtualParentRef = useRef<HTMLDivElement>(null);
  const estimateVirtualItemSize = useCallback(
    (index: number) => {
      const item = menuItems[index];

      if (isGroupHeader(item)) {
        return showGroupHeaders && (showEmptyGroups || item.filteredItemCount)
          ? 32
          : 0;
      }

      return item.subtext ? MENU_ITEM_WITH_SUBTEXT_HEIGHT : MENU_ITEM_HEIGHT;
    },
    [isGroupHeader, menuItems, showEmptyGroups, showGroupHeaders],
  );

  return {
    size: menuItems.length,
    count: menuItems.length,
    parentRef: virtualParentRef,
    getScrollElement: () => scrollElement,
    setScrollElement,
    estimateSize: estimateVirtualItemSize,
    overscan: 10,
  };
};
